import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { Box, Button, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material'
import { H3 } from '../../layouts/TextStyles'
import { ui, tone } from '../../utils/uiTokens'
import { capitalize } from '../../utils/functions'
import { ddmm, ddmmyyyy, todayIso } from '../../utils/periodDates'
import { useDirectorSubject, useHeadScope } from '../head/headScope'
import DirectorSubjectFilter from '../director/DirectorSubjectFilter'
import { Kpi, Pill, SortCell, bodyRow, btn, headRow, kpiRow, muted, page, sub, tableWrap } from '../director/ui'
import { SaCategory, SaKind, SaRow, staffActivityApi } from '../../features/activity/staffActivityApi'

// Белсенділік менторов (басшы - свои; сервис басшы - все) и пән басшыларов (вкладка сервис басшы)

type Preset = 'today' | 'week' | 'month' | 'custom'
const PRESETS: { key: Preset; id: string }[] = [
    { key: 'today', id: 'saToday' },
    { key: 'week', id: 'saWeek' },
    { key: 'month', id: 'saMonth' },
    { key: 'custom', id: 'saCustom' },
]

const MENTOR_CATS: SaCategory[] = ['CONSPECT_CHECK', 'HOMEWORK_CHECK', 'PRACTICE_ZACHET', 'CRM_STEP', 'STUDENT_VIEW', 'OTHER']
const HEAD_CATS: SaCategory[] = ['SALARY', 'CRM_STEP', 'STUDENT_VIEW', 'OTHER']

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const presetRange = (p: Preset): { from: string; to: string } => {
    const now = new Date()
    if (p === 'today') return { from: todayIso(), to: todayIso() }
    if (p === 'month') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: todayIso() }
    const monday = new Date(now)
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7))
    return { from: iso(monday), to: todayIso() }
}

type SortKey = 'name' | 'idle' | 'activeDays' | 'checks' | 'pending' | SaCategory

const sortValue = (r: SaRow, key: SortKey): number | string => {
    if (key === 'name') return `${r.lastName} ${r.firstName}`.trim().toLowerCase()
    // никогда не заходил - дальше всех
    if (key === 'idle') return r.idleDays ?? 100000
    if (key === 'activeDays') return r.activeDays
    if (key === 'checks') return r.checks
    if (key === 'pending') return r.pendingConspects + r.pendingHomeworks
    return r.counts[key] ?? 0
}

const StaffActivityPage = () => {
    const { formatMessage } = useIntl()
    const { director } = useHeadScope()
    const { subjectId } = useDirectorSubject()
    const [params, setParams] = useSearchParams()
    const kind: SaKind = director && params.get('tab') === 'heads' ? 'HEADS' : 'MENTORS'
    const [preset, setPreset] = useState<Preset>('week')
    const [custom, setCustom] = useState(presetRange('week'))
    const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'idle', dir: -1 })
    const [open, setOpen] = useState<number | null>(null)

    const range = preset === 'custom' ? custom : presetRange(preset)
    const { data, isFetching } = staffActivityApi.useGetStaffActivityQuery({
        kind,
        from: range.from,
        to: range.to,
        subjectId: director ? subjectId ?? 0 : 0,
    })
    const cats = kind === 'HEADS' ? HEAD_CATS : MENTOR_CATS

    const rows = useMemo(
        () =>
            (data?.rows ?? []).slice().sort((a, b) => {
                const x = sortValue(a, sort.key)
                const y = sortValue(b, sort.key)
                return (x < y ? -1 : x > y ? 1 : 0) * sort.dir
            }),
        [data, sort],
    )
    const toggleSort = (key: SortKey) => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : -1 }))
    const sortCell = (key: SortKey, id: string, align: 'left' | 'center' = 'center') => (
        <SortCell key={key} label={formatMessage({ id })} active={sort.key === key} dir={sort.dir} onClick={() => toggleSort(key)} align={align} />
    )

    const inactive = rows.filter((r) => r.inactive).length
    const checks = rows.reduce((s, r) => s + r.checks, 0)
    const pendingC = rows.reduce((s, r) => s + r.pendingConspects, 0)
    const pendingH = rows.reduce((s, r) => s + r.pendingHomeworks, 0)
    const colSpan = 4 + cats.length + (kind === 'MENTORS' ? 2 : 1)

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <H3>{formatMessage({ id: 'saTitle' })}</H3>
                <Box sx={{ ...sub, margin: '6px 0 16px', maxWidth: 760 }}>
                    {formatMessage({ id: kind === 'HEADS' ? 'saSubHeads' : 'saSubMentors' })}
                </Box>

                {director && (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', marginBottom: '14px' }}>
                        {(['mentors', 'heads'] as const).map((t) => (
                            <Box
                                key={t}
                                sx={chipSx((t === 'heads') === (kind === 'HEADS'))}
                                onClick={() => {
                                    setOpen(null)
                                    setParams({ tab: t }, { replace: true })
                                }}
                            >
                                {formatMessage({ id: t === 'heads' ? 'saTabHeads' : 'saTabMentors' })}
                            </Box>
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
                    <DirectorSubjectFilter />
                    {PRESETS.map((p) => (
                        <Box key={p.key} sx={chipSx(preset === p.key)} onClick={() => setPreset(p.key)}>
                            {formatMessage({ id: p.id })}
                        </Box>
                    ))}
                    {preset === 'custom' && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                type="date"
                                value={custom.from}
                                onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))}
                                inputProps={{ style: { padding: '6px 8px', fontSize: '13px' } }}
                            />
                            <span style={{ color: ui.muted }}>–</span>
                            <TextField
                                size="small"
                                type="date"
                                value={custom.to}
                                onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))}
                                inputProps={{ style: { padding: '6px 8px', fontSize: '13px' } }}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ ...muted, marginBottom: '12px' }}>
                    {data?.trackedSince
                        ? formatMessage({ id: 'saTrackedSince' }, { date: ddmmyyyy(data.trackedSince) })
                        : data && formatMessage({ id: 'saNotTracked' })}
                </Box>

                {isFetching && <LinearProgress sx={{ marginBottom: 2 }} />}

                <Box sx={kpiRow}>
                    <Kpi dark label={formatMessage({ id: 'saPeople' })} value={rows.length} />
                    <Kpi label={formatMessage({ id: 'saInactive' })} value={inactive} color={inactive ? tone.bad.text : undefined} />
                    {kind === 'MENTORS' && <Kpi label={formatMessage({ id: 'saChecks' })} value={checks} />}
                    {kind === 'MENTORS' && (
                        <Kpi label={formatMessage({ id: 'saPending' })} value={`${pendingC} / ${pendingH}`} hint={formatMessage({ id: 'saPendingHint' })} />
                    )}
                </Box>

                <Box sx={tableWrap}>
                    <Table sx={{ minWidth: kind === 'MENTORS' ? 1080 : 860 }}>
                        <TableHead sx={headRow}>
                            <TableRow>
                                {sortCell('name', 'saColName', 'left')}
                                {sortCell('idle', 'saLastActive', 'left')}
                                {sortCell('activeDays', 'saActiveDays')}
                                {kind === 'MENTORS' && sortCell('checks', 'saChecks')}
                                {cats.map((c) => sortCell(c, `saCat${c}`))}
                                {kind === 'MENTORS' ? sortCell('pending', 'saPending') : <TableCell>{formatMessage({ id: 'saSubjects' })}</TableCell>}
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <React.Fragment key={r.userId}>
                                    <TableRow sx={{ ...bodyRow, background: r.inactive ? tone.bad.bg : undefined }}>
                                        <TableCell>
                                            <b style={{ color: r.inactive ? tone.bad.text : undefined }}>
                                                {`${r.lastName} ${r.firstName}`.trim() || `#${r.userId}`}
                                            </b>
                                            {kind === 'MENTORS' && r.groupCount > 0 && (
                                                <Box sx={{ fontSize: '12px', color: ui.muted }}>{formatMessage({ id: 'saGroups' }, { n: r.groupCount })}</Box>
                                            )}
                                            {r.reasons.length > 0 && (
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', marginTop: '4px' }}>
                                                    {r.reasons.map((x) => (
                                                        <Pill key={x} tone="bad">{formatMessage({ id: `saReason${x}` })}</Pill>
                                                    ))}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <LastActive row={r} />
                                        </TableCell>
                                        <TableCell align="center"><b>{r.activeDays}</b></TableCell>
                                        {kind === 'MENTORS' && (
                                            <TableCell align="center" title={`${formatMessage({ id: 'saWeekChecks' })}: ${r.weekChecks}`}>
                                                <b style={{ color: r.reasons.includes('NO_CHECKS') ? tone.bad.text : undefined }}>{r.checks}</b>
                                            </TableCell>
                                        )}
                                        {cats.map((c) => (
                                            <TableCell key={c} align="center" sx={{ color: r.counts[c] ? undefined : `${ui.muted} !important` }}>
                                                {r.counts[c] ?? 0}
                                            </TableCell>
                                        ))}
                                        {kind === 'MENTORS' ? (
                                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                {r.pendingConspects} / {r.pendingHomeworks}
                                            </TableCell>
                                        ) : (
                                            <TableCell><SubjectNames row={r} /></TableCell>
                                        )}
                                        <TableCell align="right">
                                            <Button size="small" variant="outlined" sx={btn} onClick={() => setOpen(open === r.userId ? null : r.userId)}>
                                                {formatMessage({ id: open === r.userId ? 'dirClose' : 'saByDays' })}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {open === r.userId && (
                                        <TableRow>
                                            <TableCell colSpan={colSpan} sx={{ background: ui.surface, padding: '12px 14px' }}>
                                                <DaysChart row={r} from={range.from} to={range.to} cats={cats} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                            {!isFetching && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={colSpan} align="center" sx={{ color: ui.muted, padding: '28px' }}>
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
        </Box>
    )
}

const LastActive: React.FC<{ row: SaRow }> = ({ row }) => {
    const { formatMessage } = useIntl()
    if (!row.lastActiveAt) return <span style={{ color: tone.bad.text }}>{formatMessage({ id: 'saNever' })}</span>
    const ago = row.idleDays === 0 ? formatMessage({ id: 'saTodayShort' }) : formatMessage({ id: 'saDaysAgo' }, { n: row.idleDays })
    return (
        <Box>
            <b style={{ color: row.reasons.includes('NO_LOGIN') ? tone.bad.text : undefined }}>{ago}</b>
            <Box sx={{ fontSize: '12px', color: ui.muted }}>
                {ddmmyyyy(row.lastActiveAt.slice(0, 10))} {row.lastActiveAt.slice(11, 16)}
            </Box>
        </Box>
    )
}

const SubjectNames: React.FC<{ row: SaRow }> = ({ row }) => {
    const { locale } = useIntl()
    return <>{row.subjects.map((s) => s[`title${capitalize(locale)}`] || s.titleRu).join(', ') || '-'}</>
}

// По дням: столбик = все действия дня (без входа), точка под ним - был в системе
const DaysChart: React.FC<{ row: SaRow; from: string; to: string; cats: SaCategory[] }> = ({ row, from, to, cats }) => {
    const { formatMessage } = useIntl()
    const { data, isFetching } = staffActivityApi.useGetStaffActivityDaysQuery({ userId: row.userId, from, to })
    const days = data?.days ?? []
    const max = Math.max(1, ...days.map((d) => d.total))
    const name = `${row.lastName} ${row.firstName}`.trim() || `#${row.userId}`
    const tip = (d: (typeof days)[number]) =>
        [ddmm(d.day), ...cats.filter((c) => d.counts[c]).map((c) => `${formatMessage({ id: `saCat${c}` })}: ${d.counts[c]}`)].join('\n')

    return (
        <Box>
            <Box sx={{ ...sub, marginBottom: '10px' }}>
                <b>{formatMessage({ id: 'saDaysTitle' }, { name })}</b>
            </Box>
            {isFetching && <LinearProgress />}
            {!isFetching && days.every((d) => d.total === 0 && !d.counts.LOGIN) && (
                <Box sx={{ ...muted, marginBottom: '8px' }}>{formatMessage({ id: 'saDaysEmpty' })}</Box>
            )}
            <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '6px', minHeight: 150, paddingBottom: '4px', width: 'max-content' }}>
                    {days.map((d) => (
                        <Box key={d.day} title={tip(d)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 30 }}>
                            <Box sx={{ fontSize: '11px', fontWeight: 700, color: d.total ? ui.ink : ui.muted, marginBottom: '2px' }}>{d.total || ''}</Box>
                            <Box
                                sx={{
                                    width: 18,
                                    height: `${Math.round((d.total / max) * 110)}px`,
                                    minHeight: d.total ? 4 : 0,
                                    borderRadius: '4px 4px 0 0',
                                    background: ui.pink,
                                }}
                            />
                            <Box sx={{ width: 18, height: '2px', background: ui.border }} />
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    margin: '4px 0 2px',
                                    background: d.counts.LOGIN ? tone.ok.text : 'transparent',
                                    border: `1px solid ${d.counts.LOGIN ? tone.ok.text : ui.border}`,
                                }}
                            />
                            <Box sx={{ fontSize: '10.5px', color: ui.muted, whiteSpace: 'nowrap' }}>{ddmm(d.day)}</Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

const chipSx = (active: boolean) => ({
    padding: '7px 14px',
    borderRadius: ui.radius,
    cursor: 'pointer',
    userSelect: 'none' as const,
    fontSize: '13.5px',
    fontWeight: active ? 700 : 500,
    color: active ? ui.pink : ui.sub,
    backgroundColor: active ? ui.pinkSoft : 'transparent',
    border: `1px solid ${active ? 'transparent' : ui.border}`,
})

export default StaffActivityPage
